import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { useFormikContext, type FormikHelpers } from 'formik'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import * as Api from 'src/api'
import { CloseIcon } from 'src/assets/icons/customIcons/Close'
import { getLecturerDegreeName } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders, type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import { Modal } from 'src/tailwind/components/Modal'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { Form, useFormData } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import Select from 'src/views/components/Select'
import SemesterSelect from 'src/views/includes/SemesterSelect'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

const GRID_TWO = 'grid grid-cols-1 gap-4 md:grid-cols-2'

// Sentinel for the week filters' "Any" option. Kept non-empty so it survives the
// submit/URL round-trip (empty values are deleted and re-defaulted to the first week).
const ANY_WEEK = 'any'

const DAY_NAMES_KA = ['', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი', 'კვირა']
const DAY_NAMES_EN = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

// Row header background is keyed by day of week (Monday=1 ... Sunday=7), matching legacy Argus.
const DAY_BG_CLASSES = [
  '',
  'bg-lightGreen',
  'bg-lightBlue',
  'bg-lightYellow',
  'bg-lightPurple',
  'bg-lightRed',
  'bg-lightOrange',
  'bg-card',
]

function getDayName(dayOfWeek: number, locale: string): string {
  const names = locale === 'ka' ? DAY_NAMES_KA : DAY_NAMES_EN
  return names[dayOfWeek] ?? ''
}

function getDayBgClass(dayOfWeek: number): string {
  return DAY_BG_CLASSES[dayOfWeek] ?? 'bg-card'
}

export default function AdministratorSchedulesPage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('schedule:schedules')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

interface FormikValues {
  readonly semId: string
  readonly degree: string
  readonly buildingId: string
  readonly hourId: string
  readonly startWeek: string
  readonly endWeek: string
}

function PageContent(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams({
    semId: '',
    degree: '',
    buildingId: '',
    hourId: '',
    startWeek: '',
    endWeek: '',
  })
  const t = useTranslatable()
  const locale = useLocale()
  const activeSemester = useActiveSemester()

  React.useEffect(() => {
    const semFromUrl = searchParams.get('semId')
    if (semFromUrl != null && semFromUrl !== '') return
    if (activeSemester?.id == null) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('semId', String(activeSemester.id))
        return next
      },
      { replace: true }
    )
  }, [searchParams, activeSemester?.id, setSearchParams])

  React.useEffect(() => {
    const degreeFromUrl = searchParams.get('degree')
    if (degreeFromUrl != null && degreeFromUrl !== '') return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('degree', '1')
        return next
      },
      { replace: true }
    )
  }, [searchParams, setSearchParams])

  const semId = searchParams.get('semId') || ''
  const degree = searchParams.get('degree') || ''
  const buildingId = searchParams.get('buildingId') || ''
  const hourId = searchParams.get('hourId') || ''
  const startWeek = searchParams.get('startWeek') || ''
  const endWeek = searchParams.get('endWeek') || ''

  const initialValues: FormikValues = { semId, degree, buildingId, hourId, startWeek, endWeek }

  const breadcrumbsItems = [
    {
      page: t('schedule:schedules'),
      path: `/${locale}/administrator/administration/schedules`,
    },
  ]

  const handleFormSubmit = React.useCallback(
    (values: FormikValues) => {
      setSearchParams((params) => {
        Object.entries(values).forEach(([key, value]) => {
          if (value) {
            params.set(key, value)
          } else {
            params.delete(key)
          }
        })
        return params
      })
    },
    [setSearchParams]
  )

  const headers = useAuthenticatedHeaders()
  const { data: buildingsResponse } = useApi({
    endpoint: Api.getAdministratorListsBuildings,
    params: React.useMemo(() => ({ headers }), [headers]),
    revalidateIfStale: false,
  })

  // The grid's error boundary has no reset path of its own, so a query the API rejects —
  // an end week earlier than the start week, say — would otherwise wedge the page for good.
  // Keying it on the filter set remounts it per search, letting the next one recover.
  const gridKey = [semId, degree, buildingId, hourId, startWeek, endWeek].join('|')

  const resolvedBuildingName = React.useMemo(() => {
    const bid = searchParams.get('buildingId')
    if (!bid || !buildingsResponse?.data) return ''
    return buildingsResponse.data.find((b) => String(b.id) === bid)?.name ?? bid
  }, [searchParams, buildingsResponse?.data])

  return (
    <Section
      title={t('schedule:schedule_search')}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faCalendarAlt} />}
      breadcrubms={breadcrumbsItems}
    >
      <Form
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch buildingsResponse={buildingsResponse} />
      </Form>

      <SuspenseWrapper key={gridKey}>
        <ScheduleGridContent searchParams={searchParams} buildingName={resolvedBuildingName} />
      </SuspenseWrapper>
    </Section>
  )
}

interface FormSearchProps {
  readonly buildingsResponse: Api.getAdministratorListsBuildingsOk | undefined
}

function FormSearch({ buildingsResponse }: FormSearchProps): React.ReactElement {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const activeSemester = useActiveSemester()
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: degreesResponse } = useApi({
    endpoint: Api.getAdministratorListsDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
    revalidateIfStale: false,
  })

  const { data: hoursResponse } = useApi({
    endpoint: Api.getAdministratorListsHours,
    params: React.useMemo(() => ({ headers }), [headers]),
    revalidateIfStale: false,
  })

  const currentSemId = searchParams.get('semId') || ''
  const currentDegree = searchParams.get('degree') || ''
  const currentBuildingId = searchParams.get('buildingId') || ''
  const currentHourId = searchParams.get('hourId') || ''
  const currentStartWeek = searchParams.get('startWeek') || ''
  const currentEndWeek = searchParams.get('endWeek') || ''

  const semIdForWeeks = currentSemId ? Number(currentSemId) : undefined
  const degreeForWeeks = currentDegree ? Number(currentDegree) : undefined

  const { data: weeksResponse } = useApi({
    endpoint: Api.getAdministratorListsLearningWeeksSchedule,
    params: React.useMemo(
      () => ({
        headers,
        query: { degree: degreeForWeeks!, ...(semIdForWeeks != null ? { semId: semIdForWeeks } : {}) },
      }),
      [headers, degreeForWeeks, semIdForWeeks]
    ),
    shouldFetch: degreeForWeeks != null,
  })

  React.useEffect(() => {
    const buildingFromUrl = searchParams.get('buildingId')
    if (buildingFromUrl != null && buildingFromUrl !== '') return
    const firstBuilding = buildingsResponse?.data?.[0]
    if (!firstBuilding) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('buildingId', String(firstBuilding.id))
        return next
      },
      { replace: true }
    )
  }, [searchParams, buildingsResponse?.data, setSearchParams])

  React.useEffect(() => {
    const startFromUrl = searchParams.get('startWeek')
    if (startFromUrl != null && startFromUrl !== '') return
    const firstWeek = weeksResponse?.data?.startWeeks?.[0]
    if (!firstWeek) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('startWeek', String(firstWeek.weekNumber))
        return next
      },
      { replace: true }
    )
  }, [searchParams, weeksResponse?.data?.startWeeks, setSearchParams])

  React.useEffect(() => {
    const endFromUrl = searchParams.get('endWeek')
    if (endFromUrl != null && endFromUrl !== '') return
    const firstWeek = weeksResponse?.data?.endWeeks?.[0]
    if (!firstWeek) return
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params)
        next.set('endWeek', String(firstWeek.weekNumber))
        return next
      },
      { replace: true }
    )
  }, [searchParams, weeksResponse?.data?.endWeeks, setSearchParams])

  const degreeOptions = React.useMemo(
    () => [
      { value: '', label: t('common:any') },
      ...(degreesResponse ?? []).map((d) => ({ value: String(d.id), label: d.name })),
    ],
    [degreesResponse, t]
  )

  const buildingOptions = React.useMemo(
    () => [
      { value: '', label: t('common:any') },
      ...(buildingsResponse?.data ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    ],
    [buildingsResponse?.data, t]
  )

  const hourOptions = React.useMemo(
    () => [
      { value: '', label: t('schedule:choose_lecture_hour') },
      ...(hoursResponse?.data ?? []).map((h) => ({
        value: String(h.id),
        label: `${h.name} (${h.startTime}-${h.endTime})`,
      })),
    ],
    [hoursResponse?.data, t]
  )

  const startWeekOptions = React.useMemo(
    () => [
      { value: ANY_WEEK, label: t('common:any') },
      ...(weeksResponse?.data?.startWeeks ?? []).map((w) => ({ value: String(w.weekNumber), label: w.label })),
    ],
    [weeksResponse?.data?.startWeeks, t]
  )

  const endWeekOptions = React.useMemo(
    () => [
      { value: ANY_WEEK, label: t('common:any') },
      ...(weeksResponse?.data?.endWeeks ?? []).map((w) => ({ value: String(w.weekNumber), label: w.label })),
    ],
    [weeksResponse?.data?.endWeeks, t]
  )

  const defaultSemId = activeSemester?.id != null ? String(activeSemester.id) : ''
  const defaultDegree = '1'
  const defaultBuildingId = buildingsResponse?.data?.[0] ? String(buildingsResponse.data[0].id) : ''
  const defaultStartWeek = weeksResponse?.data?.startWeeks?.[0]
    ? String(weeksResponse.data.startWeeks[0].weekNumber)
    : ''
  const defaultEndWeek = weeksResponse?.data?.endWeeks?.[0] ? String(weeksResponse.data.endWeeks[0].weekNumber) : ''

  const handleCancel = React.useCallback(() => {
    setSearchParams({
      semId: defaultSemId,
      degree: defaultDegree,
      buildingId: defaultBuildingId,
      hourId: '',
      startWeek: defaultStartWeek,
      endWeek: defaultEndWeek,
    })
    formik.resetForm({
      values: {
        semId: defaultSemId,
        degree: defaultDegree,
        buildingId: defaultBuildingId,
        hourId: '',
        startWeek: defaultStartWeek,
        endWeek: defaultEndWeek,
      },
    })
  }, [defaultSemId, defaultBuildingId, defaultStartWeek, defaultEndWeek, setSearchParams, formik])

  const isDirty =
    formik.values.semId !== currentSemId ||
    formik.values.degree !== currentDegree ||
    formik.values.buildingId !== currentBuildingId ||
    formik.values.hourId !== currentHourId ||
    formik.values.startWeek !== currentStartWeek ||
    formik.values.endWeek !== currentEndWeek

  const canCancel =
    currentSemId === defaultSemId &&
    !currentDegree &&
    !currentBuildingId &&
    !currentHourId &&
    !currentStartWeek &&
    !currentEndWeek

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col justify-between">
      <FormError />

      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
          <SemesterSelect isFormInput />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:mt-0 md:w-[49%]">
          <SelectInput
            label={t('person:degree')}
            name="degree"
            options={degreeOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mr-0 mt-3 w-full min-w-[100px] md:!mt-3 md:mr-2 md:w-[49%]">
          <SelectInput
            label={t(['კორპუსი', 'Building'])}
            name="buildingId"
            options={buildingOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:!mt-3 md:w-[49%]">
          <SelectInput
            label={t(['საათი', 'Hour'])}
            name="hourId"
            options={hourOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mr-0 mt-3 w-full min-w-[100px] md:!mt-3 md:mr-2 md:w-[49%]">
          <SelectInput
            label={t('schedule:start_week')}
            name="startWeek"
            options={startWeekOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:!mt-3 md:w-[49%]">
          <SelectInput
            label={t('schedule:end_week')}
            name="endWeek"
            options={endWeekOptions}
            disabled={formik.isSubmitting}
          />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty}
          type="submit"
          onClick={() => formik.submitForm()}
        >
          {t('common:search')}
        </Button>

        <Button
          variant="yellow"
          type="reset"
          disabled={formik.isSubmitting || canCancel}
          className="ml-2"
          onClick={handleCancel}
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

interface ScheduleGridContentProps {
  readonly searchParams: URLSearchParams
  readonly buildingName?: string
}

function ScheduleGridContent({ searchParams, buildingName }: ScheduleGridContentProps): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  const theme = useTheme()

  const buildingId = searchParams.get('buildingId') || ''
  const degree = searchParams.get('degree') || ''
  const startWeek = searchParams.get('startWeek') || ''
  const endWeek = searchParams.get('endWeek') || ''
  const semId = searchParams.get('semId') || ''
  const hourId = searchParams.get('hourId') || ''

  // The grid API requires concrete start/end weeks, so an "Any" selection is expanded
  // to the widest available range (earliest start week / latest end week). We also need
  // the week list to know whether any weeks exist at all — when a semester has no learning
  // weeks the form can't default-fill a week, so we must show an empty state instead of
  // spinning forever. SWR dedupes this with the form's identical fetch.
  const degreeForWeeks = degree ? Number(degree) : undefined
  const semIdForWeeks = semId ? Number(semId) : undefined

  const { data: weeksResponse } = useApi({
    endpoint: Api.getAdministratorListsLearningWeeksSchedule,
    params: React.useMemo(
      () => ({
        headers,
        query: { degree: degreeForWeeks!, ...(semIdForWeeks != null ? { semId: semIdForWeeks } : {}) },
      }),
      [headers, degreeForWeeks, semIdForWeeks]
    ),
    shouldFetch: degreeForWeeks != null,
    suspense: false,
  })

  const weeksLoaded = degreeForWeeks != null && weeksResponse != null
  const startWeeksList = weeksResponse?.data?.startWeeks ?? []
  const endWeeksList = weeksResponse?.data?.endWeeks ?? []
  const hasWeeks = startWeeksList.length > 0 && endWeeksList.length > 0

  const resolvedStartWeek =
    startWeek === ANY_WEEK
      ? startWeeksList.length > 0
        ? Math.min(...startWeeksList.map((w) => w.weekNumber))
        : undefined
      : startWeek
        ? Number(startWeek)
        : undefined
  const resolvedEndWeek =
    endWeek === ANY_WEEK
      ? endWeeksList.length > 0
        ? Math.max(...endWeeksList.map((w) => w.weekNumber))
        : undefined
      : endWeek
        ? Number(endWeek)
        : undefined

  const shouldFetch = Boolean(buildingId && degree) && resolvedStartWeek != null && resolvedEndWeek != null

  const { data: gridData } = useApi({
    endpoint: Api.getAdministratorSchedulesGrid,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          buildingId: Number(buildingId),
          degree: Number(degree),
          startWeek: resolvedStartWeek!,
          endWeek: resolvedEndWeek!,
          ...(semId ? { semId: Number(semId) } : {}),
          ...(hourId ? { hourId: Number(hourId) } : {}),
        },
      }),
      [headers, buildingId, degree, resolvedStartWeek, resolvedEndWeek, semId, hourId]
    ),
    shouldFetch,
    suspense: true,
  })

  const [cellParams, setCellParams] = useSearchParams()
  const selectedScheduleId = cellParams.get('scheduleId')

  const selectedEntry = React.useMemo(() => {
    if (!selectedScheduleId || !gridData) return null
    for (const dateCell of Object.values(gridData.cells ?? {})) {
      for (const hourCell of Object.values(dateCell)) {
        for (const roomEntries of Object.values(hourCell)) {
          const found = roomEntries.find((e) => String(e.scheduleId) === selectedScheduleId)
          if (found) return found
        }
      }
    }
    return null
  }, [gridData, selectedScheduleId])

  const selectEntry = React.useCallback(
    (entry: Api.AdministratorSchedulesGridEntry | null) => {
      setCellParams(
        (params) => {
          const next = new URLSearchParams(params)
          if (entry != null) {
            next.set('scheduleId', String(entry.scheduleId))
          } else {
            next.delete('scheduleId')
          }
          return next
        },
        { replace: true }
      )
    },
    [setCellParams]
  )

  const parsedSemId = semId ? Number(semId) : undefined

  // Like legacy Argus, the grid is always drawn: its rows and columns come from the
  // semester's hours and the building's rooms, not from the lectures inside it, so an
  // empty result is an empty grid rather than an empty state. The only genuine empty
  // state is a semester with no learning weeks — that one can never fill the week
  // filters, so it would otherwise spin forever.
  if (!weeksLoaded) {
    return <Loader className="mx-auto my-5 flex" />
  }

  if (!hasWeeks) {
    return <NoContent image={Warning} header={t('error:records_not_found')} />
  }

  if (!shouldFetch || !gridData) {
    return <Loader className="mx-auto my-5 flex" />
  }

  const { dates, hours, rooms, cells, conflictCells } = gridData

  if (dates.length === 0 || rooms.length === 0) {
    return <NoContent image={Warning} header={t('error:records_not_found')} />
  }

  const conflictCellKeys = new Set(conflictCells.map((c) => `${c.date}-${c.hourId}-${c.roomId}`))

  const buildingLabel = buildingName ? `${t(['კორპუსი', 'Building'])}: ${buildingName}` : ''

  return (
    <div className="mt-4">
      <Tooltip
        id="schedule-grid-tooltip"
        place="top"
        variant={theme === 'dark' ? 'dark' : 'light'}
        className="z-[99] max-w-[300px] whitespace-pre-line shadow-[0px_1px_14px_rgba(0,0,0,0.2)]"
      />
      {/* The grid opts out of Table's own horizontal scroller so that the page itself scrolls
          in both directions. That makes the app shell the scrollport the header row and the
          day/hour column freeze against, the way the old Argus grid behaves. */}
      <Table.Table scrollable={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className="sticky left-0 top-0 z-30 min-w-[180px] bg-lightGreen text-xs font-bold shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]">
              {buildingLabel}
            </Table.Th>
            {rooms.map((room) => (
              <Table.Th
                key={room.id}
                className="sticky top-0 z-20 min-w-[80px] border-l border-borderGrey bg-card text-center text-xs shadow-[0_2px_4px_-2px_rgba(0,0,0,0.15)]"
              >
                <div className="font-bold">
                  {room.name} {room.places}
                </div>
                <RoomEquipment room={room} />
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {dates.map((dateEntry) =>
            hours
              .filter((h) => h.day === dateEntry.dayOfWeek)
              .map((hour) => (
                <Table.Tr key={`${dateEntry.date}-${hour.id}`} className="border-t border-borderGrey">
                  <Table.Td
                    className={classNames(
                      'sticky left-0 z-10 min-w-[180px] whitespace-normal p-2 text-xs text-primaryRed shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)]',
                      getDayBgClass(dateEntry.dayOfWeek)
                    )}
                  >
                    <div>{hour.name}</div>
                    <div>
                      ({getDayName(dateEntry.dayOfWeek, locale)} {hour.startTime}-{hour.endTime}):
                    </div>
                    <div className="font-bold">{dateEntry.date}</div>
                  </Table.Td>
                  {rooms.map((room) => {
                    const entries = cells?.[dateEntry.date]?.[String(hour.id)]?.[String(room.id)] ?? []
                    const hasConflict = conflictCellKeys.has(`${dateEntry.date}-${hour.id}-${room.id}`)
                    return (
                      <Table.Td
                        key={room.id}
                        data-tooltip-id={entries.length > 0 ? 'schedule-grid-tooltip' : undefined}
                        data-tooltip-content={
                          entries.length > 0
                            ? entries.map((e) => `${e.courseCode} - ${e.courseName}`).join('\n')
                            : undefined
                        }
                        className={classNames(
                          'border-l border-borderGrey px-1 py-2 text-center text-xs transition-colors',
                          {
                            'cursor-pointer hover:bg-hover-card': entries.length > 0,
                            'bg-errorMessages/15 hover:bg-errorMessages/25': hasConflict,
                          }
                        )}
                        onClick={() => {
                          if (entries.length > 0) {
                            selectEntry(entries[0]!)
                          }
                        }}
                      >
                        {entries.map((entry) => (
                          <div key={entry.scheduleId}>{entry.courseCode}</div>
                        ))}
                      </Table.Td>
                    )
                  })}
                </Table.Tr>
              ))
          )}
        </Table.Tbody>
      </Table.Table>

      {selectedEntry != null && parsedSemId != null && (
        <ScheduleEditModal entry={selectedEntry} semId={parsedSemId} onClose={() => selectEntry(null)} />
      )}
    </div>
  )
}

// ─── Room Equipment ──────────────────────────────────────────────────────────

interface RoomEquipmentProps {
  readonly room: Api.AdministratorSchedulesGridRoom
}

function RoomEquipment({ room }: RoomEquipmentProps): JSX.Element {
  const t = useTranslatable()

  const equipment = React.useMemo(
    () => [
      {
        label: t('schedule:room_computer_short'),
        available: room.hasComputer,
        tooltip: room.hasComputer ? t('schedule:room_has_computer') : t('schedule:room_has_no_computer'),
      },
      {
        label: t('schedule:room_projector_short'),
        available: room.hasProjector,
        tooltip: room.hasProjector ? t('schedule:room_has_projector') : t('schedule:room_has_no_projector'),
      },
      {
        label: t('schedule:room_camera_short'),
        available: room.hasCamera,
        tooltip: room.hasCamera ? t('schedule:room_has_camera') : t('schedule:room_has_no_camera'),
      },
    ],
    [room.hasCamera, room.hasComputer, room.hasProjector, t]
  )

  return (
    <div className="mt-1 font-normal">
      {equipment.map((item) => (
        <div
          key={item.label}
          data-tooltip-id="schedule-grid-tooltip"
          data-tooltip-content={item.tooltip}
          className="flex items-center justify-center gap-1 text-tiny leading-4 text-captionColor"
        >
          <span>{item.label}:</span>
          <FontAwesomeIcon
            icon={item.available ? ProRegularSvgIcons.faCheck : ProRegularSvgIcons.faXmark}
            className={classNames(
              'size-2.5',
              item.available ? 'text-tableGreen dark:text-primaryGreen' : 'text-errorMessages'
            )}
          />
        </div>
      ))}
    </div>
  )
}

// ─── Schedule Edit Modal ─────────────────────────────────────────────────────

interface ScheduleEditModalProps {
  readonly entry: Api.AdministratorSchedulesGridEntry
  readonly semId: number
  readonly onClose: () => void
}

interface ScheduleFormValues {
  readonly hourIds: readonly string[]
  readonly lectureTypeId: string
  readonly lectureFormatId: string
  readonly locationType: string
  readonly buildingId: string
  readonly roomId: string
  readonly locationId: string
  readonly lecturers: readonly ScheduleLecturerEntry[]
  readonly info: string
  readonly isHalfHour: boolean
  readonly isSplitLecture: boolean
  readonly massUpdate: boolean
  readonly force: boolean
}

interface ScheduleLecturerEntry {
  readonly userId: string
  readonly profileId: string
  readonly isPaid: boolean
}

const DEFAULT_SCHEDULE_FORM: ScheduleFormValues = {
  hourIds: [],
  lectureTypeId: '',
  lectureFormatId: '',
  locationType: '1',
  buildingId: '',
  roomId: '',
  locationId: '',
  lecturers: [],
  info: '',
  isHalfHour: false,
  isSplitLecture: false,
  massUpdate: false,
  force: false,
}

type LectureFormatKind = 'offline' | 'online' | 'hybrid'

function getLectureFormatKind(formatId: string): LectureFormatKind {
  if (formatId === '') return 'offline'
  const id = parseInt(formatId, 10)
  if (id === 2) return 'online'
  if (id === 3) return 'hybrid'
  return 'offline'
}

function formatScheduleDisplayDate(date: string): string {
  if (date === '') return ''
  const parts = date.split('-')
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : date
}

function formatScheduleDuplicateMessage(
  duplicate: Api.AdministratorCourseGroupScheduleDuplicateItem,
  t: ReturnType<typeof useTranslatable>
): string {
  const date = formatScheduleDisplayDate(duplicate.date)
  return t([
    `ლექცია/სემინარი ემთხვევა კურსს - ${duplicate.courseName} , მოცემულ თარიღში ${date} , ${duplicate.groupName}`,
    `Lecture/seminar overlaps with course - ${duplicate.courseName}, on date ${date}, ${duplicate.groupName}`,
  ])
}

function ScheduleDuplicatesAlert({
  duplicates,
}: {
  readonly duplicates: readonly Api.AdministratorCourseGroupScheduleDuplicateItem[] | undefined
}): JSX.Element | null {
  const t = useTranslatable()
  const messages = React.useMemo(
    () => (duplicates ?? []).map((duplicate) => formatScheduleDuplicateMessage(duplicate, t)),
    [duplicates, t]
  )

  if (messages.length === 0) return null

  if (messages.length === 1) {
    return (
      <div
        className="form-general-error my-3 flex items-center rounded-[4px] bg-seconderyRed/20 px-4 py-3 text-primaryRed"
        role="alert"
      >
        <div>
          <div className="mr-[11px] rounded-full bg-primaryRed p-1">
            <CloseIcon width="10" height="10" />
          </div>
        </div>
        <div>
          <div className="mb-1 font-bold">{t('course:lecture_conflict')}</div>
          {messages[0]}
        </div>
      </div>
    )
  }

  return (
    <div
      className="form-general-error my-3 flex items-center rounded-[4px] bg-seconderyRed/20 px-4 py-3 font-bold text-primaryRed"
      role="alert"
    >
      <div>
        <div className="mr-[11px] rounded-full bg-primaryRed p-1">
          <CloseIcon width="12" />
        </div>
      </div>
      <div>
        <div className="mb-1 font-bold">
          {t('course:lecture_conflict')}
          <ul>
            {messages.map((text, index) => (
              <li key={index}>{text}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function formatHourLabel(hour: Api.AdministratorCourseGroupScheduleHour, locale: string): string {
  const days = locale === 'ka' ? DAY_NAMES_KA : DAY_NAMES_EN
  const dayName = days[hour.day] ?? String(hour.day)
  return `${hour.name} (${dayName} ${hour.startTime}-${hour.endTime})`
}

function mapScheduleToFormValues(schedule: Api.AdministratorCourseGroupSchedule): ScheduleFormValues {
  return {
    hourIds: schedule.hour != null ? [String(schedule.hour.id)] : [],
    lectureTypeId: schedule.lectureType?.id != null ? String(schedule.lectureType.id) : '',
    lectureFormatId: schedule.lectureFormat?.id != null ? String(schedule.lectureFormat.id) : '',
    locationType:
      schedule.locationType === 2 ? '2' : schedule.locationType === 1 ? '1' : schedule.locationType == null ? '' : '',
    buildingId: schedule.building != null ? String(schedule.building.id) : '',
    roomId: schedule.room != null ? String(schedule.room.id) : '',
    locationId: schedule.location?.id != null && schedule.location.id !== '' ? String(schedule.location.id) : '',
    lecturers: (schedule.lecturers ?? []).map((l) => ({
      userId: String(l.userId),
      profileId: String(l.profileId),
      isPaid: l.isPaid ?? false,
    })),
    info: schedule.info ?? '',
    isHalfHour: schedule.isHalfHour ?? false,
    isSplitLecture: schedule.isSplitLecture ?? false,
    massUpdate: false,
    force: false,
  }
}

function buildScheduleBodyFields(
  values: ScheduleFormValues,
  hourId: number
): {
  readonly hourId: number
  readonly lectureTypeId: number
  readonly lectureFormatId: number
  readonly locationType?: number
  readonly buildingId?: number
  readonly roomId?: number
  readonly locationId?: number
  readonly isHalfHour: boolean
  readonly isSplitLecture: boolean
  readonly info?: string
  readonly lecturers: readonly { userId: string; profileId: string; isPaid: boolean }[]
} {
  const formatKind = getLectureFormatKind(values.lectureFormatId)

  const shared = {
    hourId,
    lectureTypeId: parseInt(values.lectureTypeId, 10),
    lectureFormatId: parseInt(values.lectureFormatId, 10),
    isHalfHour: values.isHalfHour,
    isSplitLecture: values.isSplitLecture,
    ...(values.info.trim() !== '' && { info: values.info.trim() }),
    lecturers: values.lecturers.map((l) => ({
      userId: l.userId,
      profileId: l.profileId,
      isPaid: l.isPaid ?? false,
    })),
  }

  if (formatKind === 'online') {
    return shared
  }

  const locationType = parseInt(values.locationType || (formatKind === 'offline' ? '1' : ''), 10)

  if (Number.isNaN(locationType)) {
    throw new Error('LOCATION_TYPE_REQUIRED')
  }
  if (locationType === 2 && values.locationId === '') {
    throw new Error('LOCATION_ID_REQUIRED')
  }

  return {
    ...shared,
    locationType,
    ...(locationType === 1 && values.buildingId !== '' && { buildingId: parseInt(values.buildingId, 10) }),
    ...(locationType === 1 && values.roomId !== '' && { roomId: parseInt(values.roomId, 10) }),
    ...(locationType === 2 && values.locationId !== '' && { locationId: parseInt(values.locationId, 10) }),
  }
}

const LECTURER_DEGREE_BY_NUMBER: Record<number, Api.LecturerDegree> = {
  1: 'LECTURER_PROFESSOR',
  2: 'LECTURER_ASSOC_PROFESSOR',
  3: 'LECTURER_ASSISTANT_PROFESSOR',
  4: 'LECTURER_INVITED_LECTURER',
  5: 'LECTURER_PROFESSOR_EMERITUS',
  6: 'LECTURER_RESP_PROFESSOR',
  7: 'LECTURER_ASSISTANT',
  8: 'LECTURER_COURSE_CURATOR',
  9: 'LECTURER_ACADEMIC_PERSONAL',
  10: 'LECTURER_TEACHER',
}

function formatAvailableLecturerDegree(degree: string | number | null | undefined): string {
  if (degree == null || degree === '') return ''
  if (typeof degree === 'number') {
    const enumVal = LECTURER_DEGREE_BY_NUMBER[degree]
    return enumVal != null ? getLecturerDegreeName(enumVal) : String(degree)
  }
  if ((Api.LecturerDegreeValues as readonly string[]).includes(degree)) {
    return getLecturerDegreeName(degree as Api.LecturerDegree)
  }
  const numericDegree = parseInt(degree, 10)
  if (!Number.isNaN(numericDegree) && LECTURER_DEGREE_BY_NUMBER[numericDegree] != null) {
    return getLecturerDegreeName(LECTURER_DEGREE_BY_NUMBER[numericDegree]!)
  }
  return degree
}

function formatAvailableLecturerName(lecturer: Api.AdministratorCourseGroupAvailableLecturer): string {
  return lecturer.personalNumber !== '' ? `${lecturer.name} (${lecturer.personalNumber})` : lecturer.name
}

function formatAvailableLecturerPickerLabel(lecturer: Api.AdministratorCourseGroupAvailableLecturer): string {
  const degreeLabel = formatAvailableLecturerDegree(lecturer.degree)
  const namePart = formatAvailableLecturerName(lecturer)
  return degreeLabel !== '' ? `${namePart} — ${degreeLabel}` : namePart
}

function ScheduleEditModal({ entry, semId, onClose }: ScheduleEditModalProps): JSX.Element {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const { isMainAdministrator } = useAdminAccess()

  const couId = entry.courseId
  const groupId = entry.groupId

  const { data: schedules, isLoading: isSchedulesLoading } = useApi({
    endpoint: Api.getAdministratorCourseGroupSchedules,
    params: React.useMemo(
      () => ({ headers, args: { couId, groupId }, query: { semId } }),
      [headers, couId, groupId, semId]
    ),
    suspense: false,
  })

  const schedule = React.useMemo(
    () => schedules?.find((s) => Number(s.id) === entry.scheduleId) ?? null,
    [schedules, entry.scheduleId]
  )

  const initialValues = React.useMemo(
    () => (schedule != null ? mapScheduleToFormValues(schedule) : DEFAULT_SCHEDULE_FORM),
    [schedule]
  )

  const [conflictTypes, setConflictTypes] = React.useState<readonly string[]>([])

  const handleSubmit = React.useCallback(
    async (values: ScheduleFormValues, formikHelpers: FormikHelpers<ScheduleFormValues>) => {
      if (schedule == null) return
      if (values.hourIds.length === 0) {
        throw { type: 'ErrorMessage' as const, message: t(['გთხოვთ აირჩიოთ საათი', 'Please choose hour']) }
      }

      let bodyFields: ReturnType<typeof buildScheduleBodyFields>
      try {
        bodyFields = buildScheduleBodyFields(values, parseInt(values.hourIds[0]!, 10))
      } catch (error) {
        if (error instanceof Error && error.message === 'LOCATION_TYPE_REQUIRED') {
          throw {
            type: 'ErrorMessage' as const,
            message: t(['გთხოვთ აირჩიოთ ლოკაციის ტიპი', 'Please choose location type']),
          }
        }
        if (error instanceof Error && error.message === 'LOCATION_ID_REQUIRED') {
          throw { type: 'ErrorMessage' as const, message: t(['გთხოვთ აირჩიოთ ლოკაცია', 'Please choose location']) }
        }
        throw error
      }

      const force = values.force

      try {
        if (!force && !values.massUpdate) {
          const conflictResult = await Api.postAdministratorCourseGroupScheduleCheckConflicts({
            headers,
            args: { couId, groupId },
            body: {
              semId,
              ...bodyFields,
              hourIds: [parseInt(values.hourIds[0]!, 10)],
              dates: [schedule.date],
              scheduleId: Number(schedule.id),
            },
          })

          if (conflictResult.hasConflicts) {
            const allConflicts: Api.AdministratorCourseGroupScheduleConflictNotice[] = []
            for (const hourEntries of Object.values(conflictResult.results)) {
              for (const ce of Object.values(hourEntries)) {
                if (ce.hasConflicts) {
                  allConflicts.push(...ce.conflicts)
                }
              }
            }
            if (allConflicts.length > 0) {
              const messages = allConflicts.flatMap((c) => c.messages)
              const types = allConflicts.map((c) => c.type)
              setConflictTypes(isMainAdministrator ? types : [])
              if (messages.length > 0) {
                throw { type: 'ErrorsObject' as const, errors: { general: messages } }
              }
            }
          }
        }

        await Api.putAdministratorCourseGroupSchedule({
          headers,
          args: { couId, groupId, id: Number(schedule.id) },
          query: { semId },
          body: {
            ...bodyFields,
            date: schedule.date,
            ...(values.massUpdate && { massUpdate: true }),
            ...(force && { force: true }),
          },
        })
        setConflictTypes([])
        toast.success(t(['ცხრილი წარმატებით განახლდა', 'Schedule updated successfully']))
        onClose()
      } catch (error) {
        if (
          error != null &&
          typeof error === 'object' &&
          'type' in error &&
          ((error as { type: string }).type === 'ErrorsObject' || (error as { type: string }).type === 'ErrorMessage')
        ) {
          throw error
        }
        const conflictMessages = getScheduleConflictMessages(error)
        if (conflictMessages != null && conflictMessages.length > 0) {
          setConflictTypes(isMainAdministrator ? getConflictTypesFromError(error) : [])
          throw { type: 'ErrorsObject' as const, errors: { general: conflictMessages } }
        }
        throw error
      } finally {
        await formikHelpers.setFieldValue('force', false)
      }
    },
    [schedule, headers, couId, groupId, semId, isMainAdministrator, onClose, t]
  )

  const modalTitle =
    schedule != null
      ? `${t(['ცხრილის დეტალები', 'Schedule details'])} - ${schedule.date}, ${t(['საგანი', 'Subject'])}: ${entry.courseName}, ${t(['ჯგუფი', 'Group'])}: ${entry.groupName}`
      : t(['ცხრილის დეტალები', 'Schedule details'])

  return (
    <Modal title={modalTitle} isModalOpen onClose={onClose} width={1200}>
      <div className="schedule-modal-scroll max-h-[55vh] min-h-[300px] overflow-y-auto px-4 py-3">
        {isSchedulesLoading ? (
          <Loader />
        ) : schedule == null ? (
          <NoContent image={Warning} header={t('error:records_not_found')} />
        ) : (
          <Form<ScheduleFormValues>
            key={schedule.id}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            classNames={{ form: 'w-full' }}
            isConfirmable
            enableReinitialize
          >
            <ScheduleEditFormContent
              headers={headers}
              couId={couId}
              groupId={groupId}
              semId={semId}
              duplicates={schedule.duplicates}
              conflictTypes={conflictTypes}
              onClearConflicts={() => setConflictTypes([])}
            />
          </Form>
        )}
      </div>
    </Modal>
  )
}

// ─── Schedule Edit Form Content ──────────────────────────────────────────────

interface ScheduleEditFormContentProps {
  readonly headers: AuthenticatedHeaders
  readonly couId: number
  readonly groupId: number
  readonly semId: number
  readonly duplicates: Api.AdministratorCourseGroupScheduleDuplicatesArray | undefined
  readonly conflictTypes: readonly string[]
  readonly onClearConflicts: () => void
}

function ScheduleEditFormContent({
  headers,
  couId,
  groupId,
  semId,
  duplicates,
  conflictTypes,
  onClearConflicts,
}: ScheduleEditFormContentProps): JSX.Element {
  const formik = useFormikContext<ScheduleFormValues>()
  const t = useTranslatable()
  const locale = useLocale()

  const scheduleConflictsPending = conflictTypes.length > 0

  const prevRoomIdRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    if (!scheduleConflictsPending) {
      prevRoomIdRef.current = formik.values.roomId
      return
    }
    const prevRoomId = prevRoomIdRef.current
    if (prevRoomId !== undefined && prevRoomId !== formik.values.roomId) {
      onClearConflicts()
    }
    prevRoomIdRef.current = formik.values.roomId
  }, [formik.values.roomId, scheduleConflictsPending, onClearConflicts])

  const { data: hoursData } = useApi({
    endpoint: Api.getAdministratorListsHours,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: lectureTypesData } = useApi({
    endpoint: Api.getAdministratorListsLectureTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: formatsData, isLoading: isFormatsLoading } = useApi({
    endpoint: Api.getAdministratorListsLectureFormats,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const hourOptions = React.useMemo(
    () =>
      (hoursData?.data ?? []).map((h) => ({
        value: String(h.id),
        label: formatHourLabel(h, locale),
      })),
    [hoursData, locale]
  )

  const lectureTypeOptions = React.useMemo(
    () => (lectureTypesData?.data ?? []).map((lt) => ({ value: String(lt.id), label: lt.name })),
    [lectureTypesData]
  )

  const formatOptions = React.useMemo(
    () => (formatsData?.data ?? []).map((f) => ({ value: String(f.id), label: f.name })),
    [formatsData]
  )

  const hourSelected = formik.values.hourIds.length > 0
  const locationType = formik.values.locationType
  const buildingId = formik.values.buildingId
  const lectureFormatId = formik.values.lectureFormatId

  const formatKind = getLectureFormatKind(lectureFormatId)
  const showLocationType = formatKind !== 'online'
  const showBuildingRoom = showLocationType && locationType === '1'
  const showOtherLocation = showLocationType && locationType === '2'

  const prevFormatRef = React.useRef(lectureFormatId)
  React.useEffect(() => {
    if (!hourSelected || lectureFormatId === '') return
    const prev = prevFormatRef.current
    prevFormatRef.current = lectureFormatId
    if (prev === lectureFormatId) return

    if (formatKind === 'online') {
      void formik.setFieldValue('locationType', '')
      void formik.setFieldValue('buildingId', '')
      void formik.setFieldValue('roomId', '')
      void formik.setFieldValue('locationId', '')
      return
    }
    if (formatKind === 'hybrid') {
      void formik.setFieldValue('locationType', '')
      void formik.setFieldValue('buildingId', '')
      void formik.setFieldValue('roomId', '')
      void formik.setFieldValue('locationId', '')
      return
    }
    void formik.setFieldValue('locationType', '1')
    void formik.setFieldValue('buildingId', '')
    void formik.setFieldValue('roomId', '')
    void formik.setFieldValue('locationId', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatKind, hourSelected, lectureFormatId])

  React.useEffect(() => {
    if (locationType === '1') return
    if (formik.values.buildingId !== '' || formik.values.roomId !== '') {
      void formik.setFieldValue('buildingId', '')
      void formik.setFieldValue('roomId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationType])

  React.useEffect(() => {
    if (showOtherLocation) return
    if (formik.values.locationId !== '') {
      void formik.setFieldValue('locationId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtherLocation])

  const prevBuildingIdRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (prevBuildingIdRef.current === null) {
      prevBuildingIdRef.current = buildingId
      return
    }
    if (prevBuildingIdRef.current !== buildingId) {
      prevBuildingIdRef.current = buildingId
      void formik.setFieldValue('roomId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId])

  const { data: buildingsData, isLoading: isBuildingsLoading } = useApi({
    endpoint: Api.getAdministratorListsBuildings,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch: hourSelected && showBuildingRoom,
    suspense: false,
  })

  const { data: roomsData, isLoading: isRoomsLoading } = useApi({
    endpoint: Api.getAdministratorListsRooms,
    params: React.useMemo(
      () => ({ headers, query: buildingId !== '' ? { buildingId: parseInt(buildingId, 10) } : {} }),
      [headers, buildingId]
    ),
    shouldFetch: hourSelected && showBuildingRoom && buildingId !== '',
    suspense: false,
  })

  const { data: scheduleLocationsData, isLoading: isScheduleLocationsLoading } = useApi({
    endpoint: Api.getAdministratorListsScheduleLocations,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch: hourSelected && showOtherLocation,
    suspense: false,
  })

  const { data: lecturersData, isLoading: isLecturersLoading } = useApi({
    endpoint: Api.getAdministratorCourseGroupScheduleLecturers,
    params: React.useMemo(
      () => ({ headers, args: { couId, groupId }, query: { semId } }),
      [headers, couId, groupId, semId]
    ),
    suspense: false,
  })

  const resolvedLecturers = React.useMemo(() => lecturersData ?? [], [lecturersData])

  const locationTypeOptions = React.useMemo(() => {
    const options = [
      { value: '1', label: t(['უნივერსიტეტი', 'University']) },
      { value: '2', label: t(['სხვა', 'Other']) },
    ]
    if (formatKind === 'hybrid') {
      return [{ value: '', label: t(['გთხოვთ აირჩიოთ', 'Please choose']) }, ...options]
    }
    return options
  }, [formatKind, t])

  const buildingOptions = React.useMemo(
    () => [
      { value: '', label: t(['გთხოვთ აირჩიოთ კორპუსი', 'Please Choose Building']) },
      ...(buildingsData?.data ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    ],
    [buildingsData, t]
  )

  const roomOptions = React.useMemo(
    () =>
      buildingId === ''
        ? [{ value: '', label: t('common:select') }]
        : [
            { value: '', label: t('common:select') },
            ...(roomsData?.data ?? []).map((r) => ({ value: String(r.id), label: r.name })),
          ],
    [buildingId, roomsData, t]
  )

  const scheduleLocationOptions = React.useMemo(() => {
    const placeholder = { value: '', label: t(['გთხოვთ აირჩიოთ ლოკაცია', 'Please Choose Location']) }
    const fromApi = (scheduleLocationsData?.data ?? [])
      .filter((l) => l.id > 0)
      .map((l) => {
        const label = l.title.trim()
        return {
          value: String(l.id),
          label: label !== '' ? label : `${t(['ლოკაცია', 'Location'])} #${l.id}`,
        }
      })
    return [placeholder, ...fromApi]
  }, [scheduleLocationsData, t])

  const lecturerByUserId = React.useMemo(() => {
    const map = new Map<string, Api.AdministratorCourseGroupAvailableLecturer>()
    for (const l of resolvedLecturers) {
      map.set(l.userId, l)
    }
    return map
  }, [resolvedLecturers])

  const availableLecturerPickerOptions = React.useMemo(
    () =>
      resolvedLecturers
        .filter((l) => !formik.values.lecturers.some((sel) => sel.userId === l.userId))
        .map((l) => ({
          value: l.userId,
          label: formatAvailableLecturerPickerLabel(l),
        })),
    [formik.values.lecturers, resolvedLecturers]
  )

  const [lecturerPickerKey, setLecturerPickerKey] = React.useState(0)

  const handleAddLecturer = React.useCallback(
    (userId: string) => {
      if (userId === '') return
      const lecturer = lecturerByUserId.get(userId)
      if (lecturer == null) return
      if (formik.values.lecturers.some((l) => l.userId === userId)) return
      void formik.setFieldValue('lecturers', [
        ...formik.values.lecturers,
        { userId: lecturer.userId, profileId: lecturer.profileId, isPaid: true },
      ])
      setLecturerPickerKey((k) => k + 1)
    },
    [formik, lecturerByUserId]
  )

  const handleRemoveLecturer = React.useCallback(
    (userId: string) => {
      void formik.setFieldValue(
        'lecturers',
        formik.values.lecturers.filter((l) => l.userId !== userId)
      )
    },
    [formik]
  )

  const handleToggleLecturerPaid = React.useCallback(
    (userId: string, isPaid: boolean) => {
      void formik.setFieldValue(
        'lecturers',
        formik.values.lecturers.map((l) => (l.userId === userId ? { ...l, isPaid } : l))
      )
    },
    [formik]
  )

  return (
    <fieldset disabled className="flex w-full flex-col gap-5">
      <ScheduleFormScrollToError />
      <FormError />
      <ScheduleDuplicatesAlert duplicates={duplicates} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto] lg:items-start">
        <SelectInput
          label={t(['ლექციის საათი', 'Lecture Hour'])}
          name="hourIds[0]"
          options={hourOptions}
          required
          menuPortalTarget={document.body}
        />
        <SelectInput
          label={t(['ლექციის ტიპი', 'Lecture Type'])}
          name="lectureTypeId"
          options={lectureTypeOptions}
          required
          menuPortalTarget={document.body}
        />
        <div className="flex flex-col gap-3 rounded-md border border-borderGrey/80 bg-[rgba(0,0,0,0.02)] px-4 py-3 dark:bg-[rgba(255,255,255,0.03)] lg:min-w-[220px]">
          <Checkbox name="isHalfHour" label={t(['ნახევარი საათის ცხრილი', 'Is Half Hour Schedule'])} />
          <Checkbox name="isSplitLecture" label={t(['გაყოფილი ლექცია', 'Is Split Lecture'])} />
        </div>
      </div>

      {hourSelected && (
        <>
          <div className={classNames(GRID_TWO, 'items-start')}>
            <SelectInput
              label={t(['ლექციის ფორმატი', 'Lecture Format'])}
              name="lectureFormatId"
              options={formatOptions}
              required
              isLoading={isFormatsLoading}
              menuPortalTarget={document.body}
            />
            <div className="mb-3">
              <label className="mb-[10px] flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
                {t(['ცხრილის ლექტორები', 'Schedule Lecturers'])}
              </label>
              <Select
                key={lecturerPickerKey}
                placeholder={t(['აირჩიეთ ლექტორები', 'Choose Lecturers'])}
                options={availableLecturerPickerOptions}
                value={null}
                onChange={(option) => {
                  const userId = (option as { value: string } | null)?.value
                  if (userId != null && userId !== '') {
                    handleAddLecturer(userId)
                  }
                }}
                isLoading={isLecturersLoading}
                isDisabled={formik.isSubmitting}
                menuPortalTarget={document.body}
                isClearable
              />
              {formik.values.lecturers.length > 0 && (
                <ul className="mt-3 divide-y divide-borderGrey rounded-lg border border-borderGrey bg-primaryWhite">
                  {formik.values.lecturers.map((entry) => {
                    const lecturer = lecturerByUserId.get(entry.userId)
                    const displayName = lecturer != null ? formatAvailableLecturerName(lecturer) : entry.userId
                    const degreeLabel = formatAvailableLecturerDegree(lecturer?.degree)
                    return (
                      <li key={entry.userId} className="flex flex-wrap items-center justify-between gap-3 p-3">
                        <div className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-primaryTextColor">{displayName}</span>
                          {degreeLabel !== '' && (
                            <span className="mt-0.5 block text-xs text-captionColor">{degreeLabel}</span>
                          )}
                        </div>
                        <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 text-sm text-primaryTextColor">
                          <input
                            type="checkbox"
                            checked={entry.isPaid}
                            onChange={(e) => handleToggleLecturerPaid(entry.userId, e.target.checked)}
                            disabled={formik.isSubmitting}
                            className="size-4 rounded border-borderGrey"
                          />
                          {t(['ანაზღაურებადი', 'Paid Lecture'])}
                        </label>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-borderGrey p-2 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed"
                          onClick={() => handleRemoveLecturer(entry.userId)}
                          aria-label={t('common:remove')}
                          disabled={formik.isSubmitting}
                        >
                          <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} className="size-4" />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>

          {showLocationType && (
            <>
              <SelectInput
                label={t(['ლოკაციის ტიპი', 'Location Type'])}
                name="locationType"
                options={locationTypeOptions}
                required
                menuPortalTarget={document.body}
              />

              {showBuildingRoom && (
                <div className={classNames(GRID_TWO, 'mt-4')}>
                  <SelectInput
                    label={t(['კორპუსი', 'Building'])}
                    name="buildingId"
                    options={buildingOptions}
                    required
                    isLoading={isBuildingsLoading}
                    menuPortalTarget={document.body}
                  />
                  <SelectInput
                    label={t('common:room')}
                    name="roomId"
                    options={roomOptions}
                    required
                    isLoading={isRoomsLoading}
                    disabled={buildingId === ''}
                    menuPortalTarget={document.body}
                  />
                </div>
              )}

              {showOtherLocation && (
                <div className="mt-4">
                  <SelectInput
                    label={t(['სხვა ლოკაცია', 'Other Location'])}
                    name="locationId"
                    options={scheduleLocationOptions}
                    required
                    isLoading={isScheduleLocationsLoading}
                    menuPortalTarget={document.body}
                  />
                </div>
              )}
            </>
          )}

          <TextareaInput
            label={t('common:additional_information')}
            name="info"
            placeholder={t('common:additional_information')}
            rows={4}
          />
        </>
      )}
    </fieldset>
  )
}

function ScheduleFormScrollToError(): null {
  const [formData] = useFormData()

  React.useEffect(() => {
    const hasErrors = formData.errors.general.length > 0 || Object.keys(formData.errors.fields).length > 0
    if (!formData.submitted || !hasErrors) return

    const timeout = window.setTimeout(() => {
      const scrollContainer = document.querySelector<HTMLElement>('.schedule-modal-scroll')
      scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' })
    }, 50)

    return () => window.clearTimeout(timeout)
  }, [formData.submitted, formData.errors])

  return null
}

function getScheduleConflictMessages(error: unknown): readonly string[] | null {
  if (error != null && typeof error === 'object' && 'type' in error) {
    if ((error as { type: string }).type === 'ScheduleConflictsDetected') {
      return (error as Api.AdministratorCourseGroupScheduleConflictsDetected).conflicts.flatMap((c) => c.messages)
    }
    if (
      (error as { type: string }).type === 'ErrorMessage' &&
      'message' in error &&
      (error as { message: string }).message === 'conflicts_detected' &&
      'conflicts' in error &&
      Array.isArray((error as { conflicts: unknown }).conflicts)
    ) {
      return (error as { conflicts: Api.AdministratorCourseGroupScheduleConflictNotice[] }).conflicts.flatMap(
        (c) => c.messages
      )
    }
  }
  return null
}

function getConflictTypesFromError(error: unknown): readonly string[] {
  if (error != null && typeof error === 'object' && 'type' in error) {
    if ((error as { type: string }).type === 'ScheduleConflictsDetected') {
      return (error as Api.AdministratorCourseGroupScheduleConflictsDetected).conflicts.map((c) => c.type)
    }
    if (
      (error as { type: string }).type === 'ErrorMessage' &&
      'conflicts' in error &&
      Array.isArray((error as { conflicts: unknown }).conflicts)
    ) {
      return (error as { conflicts: Api.AdministratorCourseGroupScheduleConflictNotice[] }).conflicts.map((c) => c.type)
    }
  }
  return []
}
